/**
 * Supervisor team rollups, their cohorts, and the measures they are ranked on.
 *
 * Extracted from the Supervisors page on 2026-09-22 so the Supervisor VIEW can show
 * the same figures. Two pages computing "team cost per claim" separately is how two
 * pages end up disagreeing about it.
 *
 * A team is the supervisor's WHOLE TREE (asked for by Shayna and Rand, decided
 * 2026-09-23): everyone below them at every level, EVERYONE — people with no claims bring
 * their full salary — and the supervisor's own labor and claims. So team cost per
 * claim is SUM(labor) / SUM(billed claims) over exactly the people the org rate is
 * built from, restricted to one branch. That reverses DECISION-LOG.md §4, which
 * counted only reports carrying claims; see its §7.
 *
 * Teams therefore overlap: a person counts toward every supervisor above them. The
 * Supervisors list does not add up to the org total, and says so.
 */
import { activeInRange, type Person } from '@/data/selectors'
import type { Coverage, GrainKey } from '@/data/coverage'
import { money2, int, pct, dec } from '@/format'
import { poolCollection } from './core'
import { COLLECTION_LABEL } from './collection'
import { rankText, titlePlural, type RankFacts } from './rankText'

/** A cohort smaller than this produces no rank. */
export const MIN_TEAM_COHORT = 4

/**
 * Is `p` in `supervisor`'s tree — below them at any level, or the supervisor
 * themself? The one definition of membership: the filter bar uses it too.
 */
export const inTree = (p: Person, supervisor: string) =>
  p.employee === supervisor || p.supervisors.includes(supervisor)

export interface Team {
  supervisor: string
  /**
   * How many levels of people sit below them: 1 = front-line (nobody under them
   * supervises), 2 = supervises front-line supervisors, and so on. Read off the
   * whole population, so a filter never moves a supervisor between cohorts.
   */
  level: number
  /**
   * The supervisor, where they are in scope and counted. Their labor and claims are in
   * the team. Null when management is left out, though they still lead the team.
   */
  self: Person | null
  /**
   * False when the supervisor has no row in this data at all — outside the RCM
   * population (20 `Not RCM` managers, including the top of the tree) or outside the
   * caller's access. Their team still rolls up; their own salary cannot.
   */
  selfInData: boolean
  /**
   * People whose direct supervisor this is: all of them, counted or not. With
   * management left out, a manager's direct reports are mostly supervisors. They are
   * still that manager's direct reports, and still open as sub-teams (review #106).
   */
  direct: Person[]
  /**
   * The whole tree, counted or not: every level below, plus the supervisor where in
   * scope. Whether a supervisor has a team at all is read off this, so the Management
   * toggle never takes a card away (review #106, 2026-10-06).
   */
  everyone: Person[]
  /** The people the figures add up: those in `everyone` that `counts` keeps. */
  members: Person[]
  /** Members with billed claims. */
  carrying: Person[]
  billed: number
  labor: number
  costPerClaim: number | null
  /** Direct reports plus the supervisor — the team one level deep, for comparison. */
  directBilled: number
  directLabor: number
  directCostPerClaim: number | null
  perEmployeePerMonth: number | null
  /** Open claims the team touched, over the pile they held: `openWorkedSum / openPileSum`. */
  openWorked: number | null
  /** The two sums behind `openWorked`, kept so teams pool into an average. See `pooledOpenWorked`. */
  openPileSum: number
  openWorkedSum: number
  collection: number | null
  closedPct: number | null
  zeroTouch: number | null
  /**
   * Facilities per member carrying claims, averaged — NOT a distinct count across the
   * team. The bundle carries a per-person count, not the identities, so two reports
   * on the same five facilities cannot be told from two on ten. The label says so.
   */
  facilitiesPerReport: number | null
  /** The supervisor's own title and tenure, where they have a row. */
  ownTenureYears: number | null
  /** The job title this team mostly holds, weighted by billed claims. */
  teamTitle: string | null
  /**
   * The supervisor's own job title: off their row, or else off their reports'
   * `supervisorTitles` for a manager outside the population. Null only where
   * DimEmployee has none, or on a bundle that predates `supervisorTitles`.
   */
  ownTitle: string | null
}

const wavg = (rows: Person[], v: (p: Person) => number | null, w: (p: Person) => number) => {
  let num = 0
  let den = 0
  for (const p of rows) {
    const val = v(p)
    const weight = w(p)
    if (val === null || weight <= 0) continue
    num += val * weight
    den += weight
  }
  return den === 0 ? null : num / den
}

const sum = (rows: Person[], v: (p: Person) => number) => rows.reduce((a, p) => a + v(p), 0)

/**
 * Levels below each supervisor, off every person's reporting line: someone at
 * position i of a line is i + 1 levels under that supervisor. Pass the UNFILTERED
 * population.
 */
export function supervisorLevels(everyone: Iterable<Person>): Map<string, number> {
  const out = new Map<string, number>()
  for (const p of everyone)
    p.supervisors.forEach((s, i) => {
      if (s !== p.employee) out.set(s, Math.max(out.get(s) ?? 0, i + 1))
    })
  return out
}

/**
 * Every supervisor's team over `people` (the filtered scope). `everyone` is the
 * unfiltered population by name — for the supervisor's own title and tenure, and for
 * levels, which a filter must not change.
 *
 * `people` decides who is on a team; `counts` decides whose labor and claims the
 * figures add up. The Supervisors page passes a pool the Management toggle has not
 * narrowed, and passes the toggle as `counts`. With management left out, every
 * supervisor keeps their card, their direct reports and their sub-teams; only the
 * figures drop the supervisors' and managers' own labor and claims (Shayna, review
 * #106, 2026-10-06: "if i exclude management i cant choose the cards that are the
 * supervisors/managers. But if i include management i have no way to see the data
 * without the supervisors metrics being included"). Leave `counts` out and everyone is
 * counted.
 *
 * `depth` chooses what a team IS for every figure on it: `'tree'` (the default) is
 * the whole reporting tree; `'direct'` is the supervisor and their direct reports
 * only. The Supervisor view switches between the two for the whole page — Shayna,
 * 2026-09-23: the switch "should be on both", not only on the people table.
 */
export function buildTeams(
  people: Person[],
  everyone: Map<string, Person>,
  depth: 'tree' | 'direct' = 'tree',
  counts: (p: Person) => boolean = () => true,
): Team[] {
  const levels = supervisorLevels(everyone.values())
  // A supervisor outside the population has no row, but their reports' lines carry
  // their title. Their own row, where they have one, wins below.
  const lineTitle = new Map<string, string>()
  for (const p of everyone.values())
    p.supervisors.forEach((s, i) => {
      const t = p.supervisorTitles[i]
      if (t && !lineTitle.has(s)) lineTitle.set(s, t)
    })
  const inScope = new Map(people.map((p) => [p.employee, p]))
  const below = new Map<string, Set<Person>>()
  const direct = new Map<string, Person[]>()
  for (const p of people) {
    // A Set, so a line that names somebody twice cannot count a person twice.
    for (const s of p.supervisors) {
      if (s === p.employee) continue
      let set = below.get(s)
      if (!set) below.set(s, (set = new Set()))
      set.add(p)
    }
    // Where the direct supervisor is outside the caller's access, the line's first link
    // is the nearest one they can see: this person is in that tree, not a direct report.
    const d = p.supervisorOutsideScope ? undefined : p.supervisors[0]
    if (d && d !== p.employee) {
      const list = direct.get(d)
      if (list) list.push(p)
      else direct.set(d, [p])
    }
  }

  return [...below.entries()].map(([supervisor, tree]) => {
    const present = inScope.get(supervisor) ?? null
    const self = present && counts(present) ? present : null
    const own = everyone.get(supervisor) ?? null
    const allReports = direct.get(supervisor) ?? []
    const reports = allReports.filter(counts)
    const wholeTree = [...tree]
    const under = depth === 'direct' ? reports : wholeTree.filter(counts)
    const members = self ? [self, ...under] : under
    const carrying = members.filter((p) => p.claims > 0)
    // Cost per claim covers EVERYONE: zero-claim salaries are the point of §7a.
    const billed = sum(members, (p) => p.claims)
    const billedStandard = sum(members, (p) => p.standardClaims)
    const labor = sum(members, (p) => p.claimLabor)
    const directTeam = self ? [self, ...reports] : reports
    const directBilled = sum(directTeam, (p) => p.claims)
    const directLabor = sum(directTeam, (p) => p.claimLabor)
    // The other rates stay on people carrying claims, now drawn from the whole tree,
    // as the Ledger's team figures are: a person with a pile but no billed claims
    // does not set the team's open-worked rate. §7a moved cost per claim only.
    const openPileSum = sum(carrying, (p) => p.openPileSum)
    const openWorkedSum = sum(carrying, (p) => p.openWorkedSum)

    const titleWeight = new Map<string, number>()
    for (const p of carrying)
      titleWeight.set(p.jobTitle, (titleWeight.get(p.jobTitle) ?? 0) + p.claims)
    let teamTitle: string | null = null
    let best = -1
    for (const [t, w] of titleWeight) if (w > best) { teamTitle = t; best = w }

    return {
      supervisor,
      level: levels.get(supervisor) ?? 1,
      self,
      selfInData: own !== null,
      direct: allReports,
      everyone: present ? [present, ...wholeTree] : wholeTree,
      members,
      carrying,
      billed,
      labor,
      // SUM / SUM over everyone, zero-claim salaries included. Never a mean of rates.
      costPerClaim: billed === 0 ? null : labor / billed,
      directBilled,
      directLabor,
      directCostPerClaim: directBilled === 0 ? null : directLabor / directBilled,
      // Billed ÷ (people carrying claims × months in the range), via each person's own
      // per-month figure — which already divides by the range.
      perEmployeePerMonth:
        carrying.length === 0
          ? null
          : sum(carrying, (p) => p.billedPerMonth ?? 0) / carrying.length,
      // Pooled, not averaged: someone holding 900 open claims and someone holding 4
      // should not have the same say in the team's rate. Pooled on the rate's OWN
      // denominator — the month-end piles in range — not on the worklist snapshot
      // (`openClaims`). Weighting by the snapshot put one team at 40.3% against a
      // true 21.3% on the 2026-09-23 bundle.
      openWorked: openPileSum === 0 ? null : openWorkedSum / openPileSum,
      openPileSum,
      openWorkedSum,
      // SUM(resolved charge) / SUM(charge), as the model's `Collected Percentage`.
      // Weighting each report's rate by billed claims instead only ties when everyone
      // bills the same dollars per claim. Each basis is the range's lagged window
      // (measures/collection.ts), so the team reads the same bill months as the org.
      collection: poolCollection(carrying.map((p) => p.collection)),
      // Over STANDARD billed claims even when the Claims switch is on Weighted: resolved
      // claims are not weighted, and the two bases never share a ratio.
      closedPct: billedStandard === 0 ? null : sum(carrying, (p) => p.resolvedClaims) / billedStandard,
      zeroTouch: wavg(carrying, (p) => p.zeroTouchShare, (p) => p.touchBase),
      facilitiesPerReport:
        carrying.length === 0 ? null : sum(carrying, (p) => p.facilities) / carrying.length,
      teamTitle,
      ownTitle: own?.jobTitle ?? lineTitle.get(supervisor) ?? null,
      ownTenureYears: own?.tenureYears ?? null,
    }
  })
}

/**
 * Which supervisor cards the Job title and Employee filters keep, on the Supervisors
 * page (review #21, 2026-10-02). Teams themselves are built without those two filters.
 *
 *   Job title — supervisors whose OWN title is picked.
 *   Employee  — that person's own card when they lead a team in `teams`; otherwise
 *               the cards of every team they are on, i.e. everyone above them.
 *
 * `ALL_EMPLOYEES` is the filter's "nobody picked" value (`ALL` in data/filters).
 */
export function supervisorCardFilter(
  teams: Team[],
  jobTitles: string[],
  employee: string,
  ALL_EMPLOYEES: string,
): (t: Team) => boolean {
  const leads = employee !== ALL_EMPLOYEES && teams.some((t) => t.supervisor === employee)
  return (t) => {
    if (jobTitles.length > 0 && !(t.ownTitle !== null && jobTitles.includes(t.ownTitle))) return false
    if (employee === ALL_EMPLOYEES) return true
    return leads ? t.supervisor === employee : t.members.some((p) => p.employee === employee)
  }
}

/**
 * `needs` names the bundle grain a row depends on. A row whose grain is absent is
 * dropped: this page draws 146 cards, so one dead row is 146 labelled tracks with no
 * value, no median tick and no bar — two of them was 292.
 */
export const TEAM_METRICS = [
  { key: 'costPerClaim', label: 'Cost per claim', lowerIsBetter: true, fmt: money2, get: (t: Team) => t.costPerClaim },
  { key: 'perEmployeePerMonth', label: 'Billed / employee / mo', lowerIsBetter: false, fmt: (v: number | null) => int(v), get: (t: Team) => t.perEmployeePerMonth },
  { key: 'collection', label: COLLECTION_LABEL, lowerIsBetter: false, fmt: (v: number | null) => pct(v), get: (t: Team) => t.collection, needs: 'collectedPct' },
  { key: 'closedPct', label: 'Claims billed vs resolved %', lowerIsBetter: false, fmt: (v: number | null) => pct(v), get: (t: Team) => t.closedPct },
  { key: 'zeroTouch', label: '0% touch share', lowerIsBetter: false, fmt: (v: number | null) => pct(v), get: (t: Team) => t.zeroTouch },
  { key: 'openWorked', label: 'Open claims worked in month %', lowerIsBetter: false, fmt: (v: number | null) => pct(v), get: (t: Team) => t.openWorked, needs: 'worklist' },
  { key: 'facilitiesPerReport', label: 'Facilities handled / person', lowerIsBetter: false, fmt: (v: number | null) => dec(v, 1), get: (t: Team) => t.facilitiesPerReport },
] as const


/** Any team measure: the shared list above, or a page's own list of the same shape. */
export interface TeamMetric {
  readonly key: string
  readonly label: string
  readonly lowerIsBetter: boolean
  readonly fmt: (v: number | null) => string
  readonly get: (t: Team) => number | null
  readonly needs?: GrainKey
}

/** The measures this bundle can populate. */
export const availableTeamMetrics = (coverage: Coverage) =>
  TEAM_METRICS.filter((r) => !('needs' in r) || coverage[r.needs as GrainKey])

/**
 * Supervisors rank against supervisors with their OWN job title: a Medicare Supervisor
 * only against Medicare Supervisors (Shayna, 2026-10-02, review comment 20). Until then
 * they ranked by level — front-line, supervisors of supervisors, 3+ levels up — which
 * put every front-line supervisor in one pool whatever they supervise. A supervisor with
 * no job title in the data has no cohort. MEASURE-RULES §10.7.
 */
export function teamCohort(teams: readonly Team[], t: Team): Team[] {
  if (!t.ownTitle) return []
  return teams.filter((o) => o.ownTitle === t.ownTitle)
}

/**
 * The figure a team is RANKED and averaged on: the metric, or null — "no data" — when
 * nobody on it carries claims. Such a team reads 0 on people carrying claims; as a
 * value it would drag the cohort's average and median down and take a real last place.
 */
export const teamValue =
  (metric: Pick<TeamMetric, 'get'>) =>
  (t: Team): number | null =>
    t.carrying.length > 0 ? metric.get(t) : null

/** The cohort as words: "Medicare Supervisors". */
export const teamGroup = (t: Team) => titlePlural(t.ownTitle)

/**
 * Where a team stands on one measure, in the same terms as a person's: the pool is
 * every ranked team in the cohort, data or not; a team with no value is "no data" at
 * the bottom; the floor is on teams WITH a value. Rank is null when not ranked.
 */
export function teamRankFacts(cohort: readonly Team[], t: Team, metric: TeamMetric): RankFacts {
  const mine = teamValue(metric)(t)
  const vals = cohort.map(teamValue(metric)).filter((v): v is number => v !== null)
  const facts = {
    of: cohort.length,
    noData: cohort.length - vals.length,
    hasValue: mine !== null,
    floor: MIN_TEAM_COHORT,
    group: teamGroup(t),
  }
  if (mine === null || vals.length < MIN_TEAM_COHORT) return { ...facts, rank: null }
  const better = new Set(vals.filter((v) => (metric.lowerIsBetter ? v < mine : v > mine)))
  return { ...facts, rank: better.size + 1 }
}

export interface TeamStanding extends RankFacts {
  rank: number
  /** 0 = best. Used for the colour band. */
  pctile: number
  /** The figure that was ranked. Under `teamRanking`, over the people active in the period. */
  value: number
}

export function teamRank(
  cohort: readonly Team[],
  t: Team,
  metric: TeamMetric,
): TeamStanding | null {
  const facts = teamRankFacts(cohort, t, metric)
  const mine = metric.get(t)
  if (facts.rank === null || mine === null) return null
  const compared = facts.of - facts.noData
  return {
    ...facts,
    rank: facts.rank,
    pctile: compared > 1 ? (facts.rank - 1) / (compared - 1) : 0,
    value: mine,
  }
}

/**
 * Everyone on these teams, each ONCE. Teams are whole trees and overlap, so summing
 * team totals counts a person again for every level above them.
 */
const distinct = (teams: readonly Team[], pick: (t: Team) => Person[]) =>
  new Set(teams.flatMap(pick))

/** Cost per claim across several teams: SUM ÷ SUM over the distinct people on them. */
export function pooledCostPerClaim(teams: readonly Team[]): number | null {
  let labor = 0
  let billed = 0
  for (const p of distinct(teams, (t) => t.members)) {
    labor += p.claimLabor
    billed += p.claims
  }
  return billed === 0 ? null : labor / billed
}

/**
 * Open-worked across several teams: worked claims over the pile, SUM ÷ SUM, over the
 * people carrying claims on them. Never weighted by billed claims — the denominator
 * is open claims, and a team billing a lot says nothing about how big a pile it holds.
 */
export function pooledOpenWorked(teams: readonly Team[]): number | null {
  let pile = 0
  let worked = 0
  for (const p of distinct(teams, (t) => t.carrying)) {
    pile += p.openPileSum
    worked += p.openWorkedSum
  }
  return pile === 0 ? null : worked / pile
}

/**
 * 0% touch share across several teams: no-touch claims over the touch base, SUM ÷
 * SUM over the people carrying claims — the same pooling as each team's own
 * `zeroTouch`. Never weighted by billed claims: the base is the resolved touch
 * buckets, a different date basis from billed (MEASURE-RULES §5).
 */
export function pooledZeroTouch(teams: readonly Team[]): number | null {
  let noTouch = 0
  let base = 0
  for (const p of distinct(teams, (t) => t.carrying)) {
    noTouch += p.noTouch
    base += p.touchBase
  }
  return base === 0 ? null : noTouch / base
}

/**
 * Collection rate across several teams: resolved charge over total charge, SUM ÷ SUM
 * over the people carrying claims on them — the same pooling as each team's own
 * `collection` and the model's `Collected Percentage`. Never the team rates weighted
 * by billed claims: that ties only where every team bills the same dollars per claim.
 */
export function pooledCollection(teams: readonly Team[]): number | null {
  return poolCollection([...distinct(teams, (t) => t.carrying)].map((p) => p.collection))
}

export function teamAverage(cohort: readonly Team[], metric: TeamMetric): number | null {
  const vals = cohort.map(teamValue(metric)).filter((v): v is number => v !== null)
  return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null
}

/**
 * One overall place for a team among comparable teams: the mean of its percentile on
 * each measure, ranked highest first. Mirrors the Ledger's supervisor rank. A team
 * with no measurable value is still in `of`, as "no data" at the bottom (§10.7).
 */
export function teamOverallRank(
  cohort: readonly Team[],
  t: Team,
  metrics: readonly TeamMetric[],
): RankFacts {
  const pctileOf = (member: Team, metric: TeamMetric): number | null => {
    const mine = teamValue(metric)(member)
    if (mine === null) return null
    const vals = cohort.map(teamValue(metric)).filter((v): v is number => v !== null)
    if (vals.length < MIN_TEAM_COHORT) return null
    const beaten = vals.filter((v) => (metric.lowerIsBetter ? v > mine : v < mine)).length
    return vals.length > 1 ? beaten / (vals.length - 1) : 1
  }
  const scored = cohort
    .map((member) => {
      const ps = metrics.map((m) => pctileOf(member, m)).filter((p): p is number => p !== null)
      return { member, score: ps.length ? ps.reduce((a, b) => a + b, 0) / ps.length : null }
    })
    .filter((x): x is { member: Team; score: number } => x.score !== null)
    .sort((a, b) => b.score - a.score)
  const i = scored.findIndex((x) => x.member === t)
  return {
    rank: i < 0 || scored.length < MIN_TEAM_COHORT ? null : i + 1,
    of: cohort.length,
    noData: cohort.length - scored.length,
    hasValue: i >= 0,
    floor: MIN_TEAM_COHORT,
    group: teamGroup(t),
  }
}

/**
 * Cards in rank order, best first: the Supervisors page's "Rank" order (Shayna, review
 * #111, 2026-10-06: "can we add rank here?"). `place` is the card's own badge, the
 * overall place among supervisors with the same job title. Ranks from different titles
 * are places in different-sized groups, so a tie on the number goes to the better
 * share of its group (2 of 24 before 2 of 9). Cards without a rank (no data, under the
 * floor, unranked) go last, in name order.
 */
export function byOverallRank(
  teams: readonly Team[],
  place: (t: Team) => RankFacts | null,
): (a: Team, b: Team) => number {
  const of = new Map<string, { rank: number; of: number } | null>()
  for (const t of teams) {
    const p = place(t)
    of.set(t.supervisor, p && p.rank !== null ? { rank: p.rank, of: p.of } : null)
  }
  return (a, b) => {
    const pa = of.get(a.supervisor) ?? null
    const pb = of.get(b.supervisor) ?? null
    if (pa === null || pb === null)
      return pa === pb ? a.supervisor.localeCompare(b.supervisor) : pa === null ? 1 : -1
    return pa.rank - pb.rank || pa.rank / pa.of - pb.rank / pb.of || a.supervisor.localeCompare(b.supervisor)
  }
}

/** Why a team on screen has no rank at all, as a card or row says it. */
export type TeamUnranked = 'supervisor terminated' | 'no active staff' | 'no job title in the data'

export interface TeamRanking {
  /** The team as it is RANKED: the same supervisor, people active in the period. Null when unranked. */
  ranked: (t: Team) => Team | null
  /** This supervisor's team as ranked, by name; null when they lead no ranked team. */
  team: (supervisor: string) => Team | null
  /** Null when the team is ranked (or merely under the floor); otherwise why not. */
  unranked: (t: Team) => TeamUnranked | null
  /** Its cohort of ranked teams; empty when the team is not ranked. */
  cohort: (t: Team) => Team[]
  rank: (t: Team, metric: TeamMetric) => TeamStanding | null
  /** The overall place; null only when the team is not ranked at all (`unranked`). */
  overall: (t: Team, metrics: readonly TeamMetric[]) => RankFacts | null
  /**
   * The hover for a rank, in words: "Rank 2 of 9 Medicare Supervisors in Jan–Aug 2026",
   * or why there is none. `metric` null for the overall rank on `metrics`.
   */
  explain: (
    t: Team,
    metric: TeamMetric | null,
    metrics: readonly TeamMetric[],
    period: string,
  ) => string
}

/**
 * How the teams on screen are ranked: on the same teams built from the people active
 * in the range, whatever the Employee status control shows (Shayna, 2026-09-25:
 * "ranking should obviously only be on active employees"; DECISION-LOG.md §8). Someone
 * who left during the range counts for the months they were active (review #61,
 * 2026-10-05).
 *
 * `rankPool` is `Scope.rankPool` — the scope's filters, people who had left before the
 * range removed, leavers entered over their active months. The figures on the card
 * still follow the control; where that moves a figure, the rank was taken on the
 * ranked figure (`TeamStanding.value`) and the card says so on hover. A supervisor
 * who had left before the range is not ranked, nor is a team with nobody active in it;
 * neither is in anybody's cohort. A team of active people none of whom carries claims
 * IS in its cohort, as "no data" (MEASURE-RULES §10.7).
 */
export function teamRanking(
  rankPool: Person[],
  everyone: Map<string, Person>,
  depth: 'tree' | 'direct' = 'tree',
  counts?: (p: Person) => boolean,
): TeamRanking {
  const left = (name: string) => {
    const p = everyone.get(name)
    return !!p && !activeInRange(p)
  }
  const teams = buildTeams(rankPool, everyone, depth, counts).filter((t) => !left(t.supervisor))
  const bySupervisor = new Map(teams.map((t) => [t.supervisor, t]))
  const ranked = (t: Team) => bySupervisor.get(t.supervisor) ?? null
  const cohort = (t: Team) => {
    const r = ranked(t)
    return r ? teamCohort(teams, r) : []
  }
  const unranked = (t: Team): TeamUnranked | null =>
    left(t.supervisor)
      ? 'supervisor terminated'
      : !ranked(t)
        ? 'no active staff'
        : !t.ownTitle
          ? 'no job title in the data'
          : null
  const overall = (t: Team, metrics: readonly TeamMetric[]) => {
    const r = ranked(t)
    return r && !unranked(t) ? teamOverallRank(cohort(t), r, metrics) : null
  }
  return {
    ranked,
    team: (supervisor) => bySupervisor.get(supervisor) ?? null,
    unranked,
    cohort,
    rank: (t, metric) => {
      const r = ranked(t)
      return r && !unranked(t) ? teamRank(cohort(t), r, metric) : null
    },
    overall,
    explain: (t, metric, metrics, period) => {
      const why = unranked(t)
      const r = ranked(t)
      if (why || !r) return `Not ranked: ${why ?? 'no active staff'}. Ranks use people active in the period.`
      const facts = metric ? teamRankFacts(cohort(t), r, metric) : overall(t, metrics)!
      return rankText(facts, period, metric ? undefined : 'the seven measures')
    },
  }
}

/**
 * The Supervisor view's seven rows, in the Ledger's order: people carrying claims
 * where the Supervisors page has facilities, and team cost per claim last. The
 * overall rank on that view averages these same rows, so the two always agree.
 */
export const SUPERVISOR_VIEW_METRICS: readonly TeamMetric[] = [
  { key: 'workers', label: 'People carrying claims', lowerIsBetter: false, fmt: (v) => (v === null ? '—' : String(Math.round(v))), get: (t) => t.carrying.length },
  { key: 'perEmployeePerMonth', label: 'Billed claims / employee / mo', lowerIsBetter: false, fmt: (v) => int(v), get: (t) => t.perEmployeePerMonth },
  { key: 'collection', label: COLLECTION_LABEL, lowerIsBetter: false, fmt: (v) => pct(v), get: (t) => t.collection, needs: 'collectedPct' },
  { key: 'closedPct', label: 'Claims billed vs resolved %', lowerIsBetter: false, fmt: (v) => pct(v), get: (t) => t.closedPct },
  { key: 'zeroTouch', label: '0% touch share', lowerIsBetter: false, fmt: (v) => pct(v), get: (t) => t.zeroTouch },
  { key: 'openWorked', label: 'Open claims worked in month %', lowerIsBetter: false, fmt: (v) => pct(v, 0), get: (t) => t.openWorked, needs: 'worklist' },
  { key: 'costPerClaim', label: 'Team cost / claim', lowerIsBetter: true, fmt: money2, get: (t) => t.costPerClaim },
]

/** The Supervisor view's rows this bundle can populate. */
export const availableSupervisorViewMetrics = (coverage: Coverage): readonly TeamMetric[] =>
  SUPERVISOR_VIEW_METRICS.filter((r) => !r.needs || coverage[r.needs])
